// src/utils/leafletUtils.js
import { DUTCH_CITIES_COORDINATES, NETHERLANDS_CENTER, NETHERLANDS_BOUNDS } from '../constants/coordinates.js';
import { isLeadClassificationForAdmin } from '../constants/businessLogic.js';
import { identifyNiche, extractCustomerCode, extractLocation } from './dashboardUtils.js';
import { getPriceForDate } from './supabaseHelpers.js';

/**
 * Escape HTML special characters to prevent XSS
 */
const escapeHtml = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Get coordinates for a Dutch city
 * @param {string} cityName - Name of the city
 * @returns {Object|null} - {lat, lng} or null if not found
 */
export const getCityCoordinates = (cityName) => {
  if (!cityName) return null;
  
  // Direct lookup
  const direct = DUTCH_CITIES_COORDINATES[cityName];
  if (direct) return direct;
  
  // Case-insensitive lookup
  const normalizedCity = cityName.trim();
  const cityKey = Object.keys(DUTCH_CITIES_COORDINATES).find(
    key => key.toLowerCase() === normalizedCity.toLowerCase()
  );
  
  return cityKey ? DUTCH_CITIES_COORDINATES[cityKey] : null;
};

/**
 * Extract location from campaign name and get coordinates
 * Uses extractLocation from dashboardUtils for consistency
 * @param {string} campaignName - Campaign name from call data
 * @returns {Object|null} - {city, lat, lng} or null
 */
export const extractLocationWithCoordinates = (campaignName) => {
  if (!campaignName) return null;

  // Use centralized extractLocation function
  let location = extractLocation(campaignName);

  // Get coordinates for the location
  let coordinates = getCityCoordinates(location);

  // Spelling fallback for common variations
  if (!coordinates) {
    const spellingFallbacks = { 'Maarsen': 'Maarssen' };
    const fallbackCity = spellingFallbacks[location];
    if (fallbackCity) {
      coordinates = getCityCoordinates(fallbackCity);
      if (coordinates) location = fallbackCity;
    }
  }

  // Fallback: search for known Dutch cities in original campaign
  if (!coordinates) {
    const originalLower = campaignName.toLowerCase();
    for (const [cityName, coords] of Object.entries(DUTCH_CITIES_COORDINATES)) {
      if (originalLower.includes(cityName.toLowerCase())) {
        location = cityName;
        coordinates = coords;
        break;
      }
    }
  }

  if (coordinates) {
    return {
      city: location,
      lat: coordinates.lat,
      lng: coordinates.lng
    };
  }
  
  return null;
};


/**
 * Process call data and group by location with coordinates (with is_recurring logic)
 * @param {Array} calls - Array of call objects
 * @param {Array} customerTargets - Array of customer target objects for pricing
 * @param {Object} priceMap - Date-based pricing map from customer_prices table
 * @returns {Array} - Array of location objects with call data
 */
export const groupCallsByLocation = (calls, customerTargets = [], priceMap = null) => {
  const locationStats = new Map();
  
  calls.forEach(call => {
    const locationData = extractLocationWithCoordinates(call.campaign);
    
    if (locationData) {
      const key = `${locationData.lat},${locationData.lng}`;
      
      if (!locationStats.has(key)) {
        locationStats.set(key, {
          city: locationData.city,
          lat: locationData.lat,
          lng: locationData.lng,
          totalCalls: 0,
          leadCalls: 0,
          uniqueCallers: 0,
          // Eigen unique-telling per (campaign, from) tuple binnen filter-periode.
          uniqueSet: new Set(),
          investment: 0,
          classifications: {},
          nicheCounts: {},
          nicheUniqueSets: {},
          campaigns: new Set(),
          customers: new Set()
        });
      }

      const stats = locationStats.get(key);
      stats.totalCalls++;

      const campaignName = call.campaign || '';
      const fromKey = call.from && call.from !== 'anonymous'
        ? call.from
        : (call.from === 'anonymous' ? `anonymous:${call.start}` : null);
      const uniqKey = fromKey && campaignName ? `${campaignName}|${fromKey}` : null;
      if (uniqKey) {
        stats.uniqueSet.add(uniqKey);
      }
      
      // Track lead classifications using AdminDashboard logic
      const classification = call.classification || 'Unknown';
      const isLead = isLeadClassificationForAdmin(classification, call.is_recurring);
      if (isLead) {
        stats.leadCalls++;
      }
      
      // Track classifications
      stats.classifications[classification] = (stats.classifications[classification] || 0) + 1;

      // Track niche counts (calls/unique/leads/revenue) - use same logic as AdminDashboard
      const niche = identifyNiche(call.campaign || '');
      
      // Calculate revenue the same way as the table: only for leads, using date-based pricing
      // Use AdminDashboard-specific lead logic (recurring calls with '-' are non-leads)
      let callRevenue = 0;

      if (isLead) {
        // Extract customer code from campaign
        const customerCode = extractCustomerCode(call.campaign);
        if (customerCode) {
          // Try date-based price first (from customer_prices table)
          if (priceMap) {
            const dateBasedPrice = getPriceForDate(priceMap, customerCode, call.start);
            if (dateBasedPrice !== null) {
              callRevenue = dateBasedPrice;
            } else {
              // Fallback to static price from customer_targets
              const target = customerTargets.find(ct => ct.customer_code === customerCode);
              callRevenue = target ? (target.price || 0) : 0;
            }
          } else {
            // No priceMap, use static price from customer_targets
            const target = customerTargets.find(ct => ct.customer_code === customerCode);
            callRevenue = target ? (target.price || 0) : 0;
          }
        }
      }
      
      // Add revenue to total investment for this location
      stats.investment += callRevenue;
      
      const nicheKey = niche || 'unknown';
      if (!stats.nicheCounts[nicheKey]) {
        stats.nicheCounts[nicheKey] = { calls: 0, unique: 0, leads: 0, revenue: 0 };
        stats.nicheUniqueSets[nicheKey] = new Set();
      }
      stats.nicheCounts[nicheKey].calls++;
      if (uniqKey) {
        stats.nicheUniqueSets[nicheKey].add(uniqKey);
      }
      if (isLead) {
        stats.nicheCounts[nicheKey].leads++;
      }
      stats.nicheCounts[nicheKey].revenue += callRevenue;
      
      // Track campaigns and customers
      if (call.campaign) stats.campaigns.add(call.campaign);
      
      // Extract customer code
      const custCode = extractCustomerCode(call.campaign);
      if (custCode) stats.customers.add(custCode);
    }
  });
  
  // Convert Map to Array and add calculated fields
  const result = Array.from(locationStats.values()).map(location => {
    // Resolve unique counts from Sets
    const uniqueCallers = location.uniqueSet ? location.uniqueSet.size : 0;
    const nicheCounts = { ...location.nicheCounts };
    Object.keys(nicheCounts).forEach(n => {
      const set = location.nicheUniqueSets && location.nicheUniqueSets[n];
      nicheCounts[n] = { ...nicheCounts[n], unique: set ? set.size : 0 };
    });
    return {
      ...location,
      uniqueCallers,
      nicheCounts,
      uniqueSet: undefined,
      nicheUniqueSets: undefined,
      campaigns: Array.from(location.campaigns),
      customers: Array.from(location.customers),
      leadRate: location.totalCalls > 0 ? (location.leadCalls / location.totalCalls * 100) : 0,
      dominantClassification: Object.entries(location.classifications)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || 'Unknown'
    };
  });
  
  return result;
};

/**
 * Get map configuration for Netherlands
 * @returns {Object} - Leaflet map configuration
 */
export const getMapConfig = () => ({
  center: NETHERLANDS_CENTER,
  zoom: 7,
  bounds: NETHERLANDS_BOUNDS,
  maxBounds: NETHERLANDS_BOUNDS,
  maxBoundsViscosity: 1.0,
  minZoom: 6,
  maxZoom: 13
});

/**
 * Generate marker color and size based on view mode and call data (INCREASED SIZES)
 * @param {Object} locationData - Location data with call statistics
 * @param {string} viewMode - Current view mode ('leads', 'unique', 'calls')
 * @param {Array} allLocationData - All location data for scaling
 * @returns {Object} - {color, size}
 */
export const getMarkerStyling = (locationData, viewMode, allLocationData) => {
  let value, maxValue, color;
  
  switch (viewMode) {
    case 'leads':
      value = locationData.leadCalls;
      maxValue = Math.max(...allLocationData.map(l => l.leadCalls), 1);
      color = '#D02E17'; // Red for leads (matching dashboard)
      break;
      
    case 'unique':
      value = locationData.uniqueCallers; // Now using is_recurring = false count
      maxValue = Math.max(...allLocationData.map(l => l.uniqueCallers), 1);
      color = '#31B9CE'; // Cyan for unique callers (matching dashboard)
      break;
      
    case 'revenue':
      value = locationData.investment || 0; // Revenue per location
      maxValue = Math.max(...allLocationData.map(l => l.investment || 0), 1);
      color = '#10B981'; // Green for revenue (matching dashboard)
      break;
      
    case 'calls':
    default:
      value = locationData.totalCalls;
      maxValue = Math.max(...allLocationData.map(l => l.totalCalls), 1);
      color = '#5F5E5E'; // Gray for total calls (matching dashboard)
      break;
  }
  
  // Calculate size based on value relative to max - INCREASED SIZES
  const minSize = 12; // was 8
  const maxSize = 40; // was 32
  const size = Math.max(minSize, minSize + ((value / maxValue) * (maxSize - minSize)));
  
  return { color, size: Math.round(size) };
};

/**
 * Generate marker size based on call volume (INCREASED SIZES)
 * @param {Object} locationData - Location data with call statistics
 * @returns {number} - Marker radius in pixels
 */
export const getMarkerSize = (locationData) => {
  const { totalCalls } = locationData;
  
  if (totalCalls === 0) return 8;        // was 5
  if (totalCalls < 5) return 14;         // was 8  
  if (totalCalls < 15) return 20;        // was 12
  if (totalCalls < 30) return 26;        // was 16
  return 32;                             // was 20
};

/**
 * Generate popup content for a location marker (minimalistic version)
 * @param {Object} locationData - Location data with call statistics
 * @param {string} viewMode - Current view mode ('leads', 'unique', 'calls')
 * @returns {string} - HTML content for popup
 */
export const generatePopupContent = (locationData, viewMode = 'calls', isAdmin = false) => {
  const { 
    city, 
    totalCalls, 
    leadCalls, 
    uniqueCallers,
    nicheCounts = {}
  } = locationData;
  
  // Get the relevant metric based on view mode
  let totalValue, totalIcon, totalLabel;
  switch (viewMode) {
    case 'leads':
      totalValue = leadCalls;
      totalIcon = '🎯';
      totalLabel = 'leads';
      break;
    case 'unique':
      totalValue = uniqueCallers;
      totalIcon = '📱';
      totalLabel = 'uniek';
      break;
    case 'revenue':
      totalValue = Math.round(locationData.investment || 0);
      totalIcon = '💰';
      totalLabel = 'omzet';
      break;
    case 'calls':
    default:
      totalValue = totalCalls;
      totalIcon = '📞';
      totalLabel = 'totaal';
      break;
  }
  
  // For admin dashboard, show per-niche breakdown
  let nicheLines = '';
  if (isAdmin) {
    // Filter niches that have the relevant metric > 0
    const nicheEntries = Object.entries(nicheCounts)
      .filter(([, v]) => {
        switch (viewMode) {
          case 'leads': return (v.leads || 0) > 0;
          case 'unique': return (v.unique || 0) > 0;
          case 'revenue': return (v.revenue || 0) > 0;
          case 'calls':
          default: return (v.calls || 0) > 0;
        }
      })
      .map(([n, v]) => {
        let nicheValue;
        
        // Get niche-specific emoji (matching identifyNiche function names)
        let nicheEmoji;
        switch (n) {
          case 'Ontstoppen':
            nicheEmoji = '🪠';
            break;
          case 'Loodgieter':
            nicheEmoji = '🔧';
            break;
          case 'Dakdekker':
            nicheEmoji = '🏠';
            break;
          case 'Schoorsteenveger (NL)':
            nicheEmoji = '🏗️';
            break;
          case 'UK Projects':
            nicheEmoji = '🇬🇧';
            break;
          case 'USA Projects':
            nicheEmoji = '🇺🇸';
            break;
          case 'Niet Ingesteld':
            nicheEmoji = '❌';
            break;
          case 'Other':
            nicheEmoji = '❓';
            break;
          default:
            nicheEmoji = '🏷️';
            break;
        }
        
        switch (viewMode) {
          case 'leads':
            nicheValue = v.leads || 0;
            break;
          case 'unique':
            nicheValue = v.unique || 0;
            break;
          case 'revenue':
            nicheValue = Math.round(v.revenue || 0);
            break;
          case 'calls':
          default:
            nicheValue = v.calls || 0;
            break;
        }
        return `<div>${nicheEmoji} ${nicheValue}</div>`;
      })
      .join('');
    
    if (nicheEntries) {
      nicheLines = `<div style="margin-top:8px; font-weight: 600;">Per niche</div>${nicheEntries}`;
    }
  }

  return `
    <div style="padding: 8px 10px; font-family: var(--font-display, system-ui, sans-serif); min-width: 90px;">
      <div style="margin: 0 0 4px 0; color: var(--color-neutral-900); font-size: 12px; font-weight: 600; line-height: 1.2; white-space: nowrap;">${escapeHtml(city)}</div>
      <div style="font-size: 11px; color: var(--color-neutral-500); line-height: 1.4; white-space: nowrap;">
        <div style="display: flex; align-items: center; gap: 3px;">${totalIcon} ${totalValue} ${totalLabel}</div>
        ${nicheLines ? `<div style="margin-top: 4px; padding-top: 4px; border-top: 1px solid var(--color-neutral-200);">${nicheLines.replace('<div style="margin-top:8px; font-weight: 600;">Per niche</div>', '')}</div>` : ''}
      </div>
    </div>
  `;
};
